import {
  findParentById,
  isTextNode,
  normalizeClassList,
  reorderChild,
} from './htmlEditorUtils.js';
import {
  getManagedNodeCapabilities,
  isManagedEditorDecoration,
} from './htmlCollectionUtils.js';
import {
  EDITOR_MODES,
  isSimpleSelectableNode,
} from './simpleEditorMode.js';

const FRIENDLY_NAMES = {
  a: 'Link',
  article: 'Section',
  blockquote: 'Quote',
  figcaption: 'Caption',
  figure: 'Image section',
  footer: 'Footer',
  h1: 'Main heading',
  h2: 'Section heading',
  h3: 'Subheading',
  h4: 'Heading',
  h5: 'Heading',
  h6: 'Heading',
  header: 'Introduction',
  img: 'Image',
  li: 'List item',
  main: 'Main content',
  ol: 'Numbered list',
  p: 'Paragraph',
  projects: 'Projects',
  section: 'Section',
  skills: 'Skills',
  table: 'Table',
  ul: 'List',
};

const firstText = (node) => {
  if (!node) return '';
  if (isTextNode(node)) return String(node.content || '').replace(/\s+/g, ' ').trim();

  for (const child of node.children || []) {
    const text = firstText(child);
    if (text) return text;
  }
  return '';
};

const shortText = (value, maxLength = 54) => {
  const text = String(value || '').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}...` : text;
};

const nodeLabel = (node, simpleMode) => {
  if (simpleMode) return FRIENDLY_NAMES[node.type] || 'Content';
  const classes = normalizeClassList(node.attributes?.class);
  return `${node.type}${classes.slice(0, 2).map((name) => `.${name}`).join('')}`;
};

export const getPhoneRearrangeItems = (
  root,
  { editorMode = EDITOR_MODES.SIMPLE } = {}
) => {
  if (!root || isTextNode(root)) return [];
  const simpleMode = editorMode === EDITOR_MODES.SIMPLE;
  const items = [];

  const visit = (node, visibleDepth) => {
    if (!node || isTextNode(node) || isManagedEditorDecoration(node)) return;

    const visible = simpleMode ? isSimpleSelectableNode(node) : true;
    const nextDepth = visible ? visibleDepth + 1 : visibleDepth;
    if (visible) {
      const parent = findParentById(root, node.id);
      const elementSiblings = (parent?.children || []).filter((child) => !isTextNode(child));
      const siblingIndex = elementSiblings.findIndex((child) => child.id === node.id);
      const capabilities = getManagedNodeCapabilities(node);
      items.push({
        id: node.id,
        type: node.type,
        label: nodeLabel(node, simpleMode),
        summary: shortText(firstText(node)),
        depth: visibleDepth,
        canMoveUp: capabilities.canMove && siblingIndex > 0,
        canMoveDown: capabilities.canMove
          && siblingIndex >= 0
          && siblingIndex < elementSiblings.length - 1,
      });
    }

    // Linked collections move as complete units; their generated internals stay hidden.
    if (node.type === 'projects' || node.type === 'skills') return;
    (node.children || []).forEach((child) => visit(child, nextDepth));
  };

  (root.children || []).forEach((child) => visit(child, 0));
  return items;
};

export const moveNodeByVisibleSibling = (root, nodeId, offset) => {
  if (!root || !nodeId || ![-1, 1].includes(offset)) return root;
  const parent = findParentById(root, nodeId);
  if (!parent) return root;

  const elementSiblings = (parent.children || []).filter((child) => !isTextNode(child));
  const visibleIndex = elementSiblings.findIndex((child) => child.id === nodeId);
  const target = elementSiblings[visibleIndex + offset];
  if (visibleIndex < 0 || !target) return root;

  const fromIndex = parent.children.findIndex((child) => child.id === nodeId);
  const targetIndex = parent.children.findIndex((child) => child.id === target.id);
  return reorderChild(root, parent.id, fromIndex, targetIndex);
};
